/**
 * Price precedent — what this customer last paid for the same spec, at three
 * densities (docs/features/price-precedent/01_product_brief.md §11):
 *
 * - `line`    one sentence, for an exceptions card;
 * - `compact` the nearest few sold rows, for the catalogue price preview;
 * - `full`    the history table, for the estimate detail page.
 *
 * Every density renders the two non-recorded states explicitly. A genuine
 * first order (`none`) and a lookup that could not run (`unavailable`) must
 * never look like each other, and neither may look like an empty table — the
 * same rule the estimate detail page's `coverage` discriminators enforce.
 *
 * The full table also renders the partial states the backend reports rather
 * than infers: which tiers the lookup could key on (`keyed_on`) with the
 * server's `note` when that is only the product code, history that exists
 * but not near this quantity, and — the common case while the structural key
 * covers few items — no order recognised at all, which offers search and link.
 */
import { Link } from "react-router";
import type {
  PrecedentCandidate,
  PrecedentLine,
  PrecedentRow,
  PricePrecedent,
  PricePrecedentView,
} from "@shared-core/data/price-precedent-types";
import {
  formatPrecedentDate,
  formatSignedPct,
  formatUnitPence,
  precedentDeltaPct,
} from "@shared-core/domain/price-precedent";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { formatCmPct } from "@shared-core/domain/commercial-exceptions";
import { PrecedentSearchAndLink } from "./PrecedentSearchAndLink";

const MATCH_LABEL: Record<PrecedentRow["match"], string> = {
  manual: "linked by hand",
  catalogue_exact: "same catalogue spec",
  structural: "same spec",
  customer_ref: "same product code",
  similar: "similar spec",
};

/** Sign-aware tone: dearer than last time reads warm, cheaper reads cool. */
function deltaTone(pct: number): string {
  if (Math.abs(pct) < 0.5) return "text-muted-foreground";
  return pct > 0
    ? "text-amber-700 dark:text-amber-400"
    : "text-sky-700 dark:text-sky-400";
}

function DeltaChip({ pct }: { pct: number | null }) {
  if (pct == null) return null;
  return (
    <span className={`tabular-nums font-medium ${deltaTone(pct)}`}>
      {Math.abs(pct) < 0.5
        ? "same as last time"
        : `${formatSignedPct(pct)} vs last`}
    </span>
  );
}

function NonRecorded({ coverage }: { coverage: "none" | "unavailable" }) {
  return (
    <span className="text-xs text-muted-foreground">
      {coverage === "none"
        ? "No previous orders for this spec from this customer"
        : "Precedent unavailable — no customer or spec to match on"}
    </span>
  );
}

// ── line ────────────────────────────────────────────────────────────────────

/** The card's line. Cards key on the quote's product code (Ref4) — the only
 *  spec tier the estimate fact carries — so the sentences say so. */
export function PricePrecedentLine({ line }: { line: PrecedentLine }) {
  if (!line.nearest) {
    return (
      <span className="text-xs text-muted-foreground">
        {line.coverage === "unavailable"
          ? "No product code on this quote to match previous orders against"
          : line.coverage === "recorded"
            ? "No recent order at this quantity for this product code"
            : "No previous orders for this product code from this customer"}
      </span>
    );
  }
  const n = line.nearest;
  return (
    <span className="text-xs text-muted-foreground">
      <span aria-hidden>↳ </span>
      Last sold {n.quantity.toLocaleString()} @{" "}
      {formatQuotedPrice(n.price_pence)}
      {" · "}
      {formatPrecedentDate(n.date)}
      {line.delta_pct != null && (
        <>
          {" · this quote "}
          <DeltaChip pct={line.delta_pct} />
        </>
      )}
    </span>
  );
}

// ── compact ─────────────────────────────────────────────────────────────────

export function PricePrecedentCompact({
  precedent,
  quotedUnitPence,
  onMatch,
}: {
  precedent: PricePrecedent;
  /** The current quote's unit price, for the delta against `nearest`. */
  quotedUnitPence: number | null;
  /** Offered when the nearest sold price can be matched with one click. */
  onMatch?: (row: PrecedentRow) => void;
}) {
  if (precedent.coverage !== "recorded") {
    return (
      <div className="rounded-md border border-dashed border-border px-3 py-2">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Previously sold
        </div>
        <NonRecorded coverage={precedent.coverage} />
      </div>
    );
  }
  const sold = (precedent.rows ?? [])
    .filter((r) => r.outcome === "ordered")
    .sort(
      (a, b) =>
        Math.abs(a.quantity - precedent.quantity) -
          Math.abs(b.quantity - precedent.quantity) ||
        b.date.localeCompare(a.date),
    )
    .slice(0, 3);
  const nearest = precedent.nearest;
  const delta =
    quotedUnitPence == null
      ? null
      : precedentDeltaPct(quotedUnitPence, nearest);
  return (
    <div className="rounded-md border border-border px-3 py-2 space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Previously sold to {precedent.customer_name}
        </div>
        <DeltaChip pct={delta} />
      </div>
      <ul className="space-y-0.5 text-sm">
        {sold.map((r) => (
          <li
            key={`${r.reference}-${r.date}`}
            className={`flex items-baseline justify-between gap-3 ${
              nearest && r.reference === nearest.reference ? "font-medium" : ""
            }`}
          >
            <span className="tabular-nums">
              {r.quantity.toLocaleString()} @ {formatQuotedPrice(r.price_pence)}
              <span className="text-muted-foreground">
                {" "}
                · {formatUnitPence(r.unit_price_pence)}/unit
              </span>
            </span>
            <span className="text-xs text-muted-foreground">
              {formatPrecedentDate(r.date)}
            </span>
          </li>
        ))}
      </ul>
      {onMatch && nearest && (
        <button
          type="button"
          className="text-xs font-medium text-primary hover:underline"
          onClick={() => onMatch(nearest)}
        >
          Match last price ({formatQuotedPrice(nearest.price_pence)} for{" "}
          {nearest.quantity.toLocaleString()})
        </button>
      )}
    </div>
  );
}

// ── full ────────────────────────────────────────────────────────────────────

const TIER_LABEL: Record<
  NonNullable<PricePrecedentView["keyed_on"]>[number],
  string
> = {
  catalogue_exact: "the catalogue spec",
  structural: "the specification",
  customer_ref: "the product code",
};

export function PricePrecedentTable({
  precedent,
  quotedUnitPence,
  onSearchCandidates,
  onLinkPrecedent,
  linkError = null,
}: {
  precedent: PricePrecedentView;
  quotedUnitPence: number | null;
  /** Search and link — offered when no order is recognised and the lookup
   *  has an identity a link can be recorded against. */
  onSearchCandidates?: (query: string) => Promise<PrecedentCandidate[]>;
  onLinkPrecedent?: (candidate: PrecedentCandidate) => void;
  linkError?: string | null;
}) {
  const rows = precedent.rows ?? [];
  const keyedOn = precedent.keyed_on ?? [];
  const nearest = precedent.nearest ?? null;
  const lastQuoted = precedent.last_quoted ?? null;
  const customer = precedent.customer_name ?? "this customer";
  const search =
    precedent.unrecognised &&
    precedent.spec_identity != null &&
    onSearchCandidates != null &&
    onLinkPrecedent != null
      ? { onSearch: onSearchCandidates, onLink: onLinkPrecedent }
      : null;
  const refreshed = precedent.refreshed_at
    ? `refreshed ${formatPrecedentDate(precedent.refreshed_at)}`
    : null;
  const same = (a: PrecedentRow | null, b: PrecedentRow) =>
    a != null && a.reference === b.reference && a.date === b.date;

  return (
    <section aria-label="Price precedent" className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h4 className="text-sm font-semibold">
          Price precedent
          {precedent.spec_label && (
            <span className="ml-2 font-normal text-muted-foreground">
              {customer} · {precedent.spec_label}
            </span>
          )}
        </h4>
        <div className="text-xs text-muted-foreground">
          last {precedent.window_months} months · ±
          {precedent.quantity_tolerance_pct}% quantity
          {refreshed ? ` · ${refreshed}` : ""}
        </div>
      </div>

      {/* The note already names a partial key; the tier list is for the rest. */}
      {keyedOn.length > 0 && !precedent.note && (
        <p className="text-xs text-muted-foreground">
          Matched on {keyedOn.map((t) => TIER_LABEL[t]).join(", ")}.
        </p>
      )}
      {precedent.note && (
        <p className="text-xs text-muted-foreground">{precedent.note}</p>
      )}

      {precedent.coverage === "unavailable" ? (
        !precedent.note && <NonRecorded coverage="unavailable" />
      ) : precedent.coverage === "none" ? (
        search ? (
          <PrecedentSearchAndLink
            message={`No previous order recognised for this spec from ${customer}.`}
            onSearch={search.onSearch}
            onLink={search.onLink}
            linkError={linkError}
          />
        ) : (
          <NonRecorded coverage="none" />
        )
      ) : (
        <>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 text-left font-medium">Date</th>
                  <th className="px-3 py-2 text-right font-medium">Qty</th>
                  <th className="px-3 py-2 text-right font-medium">Price</th>
                  <th className="px-3 py-2 text-right font-medium">Per unit</th>
                  <th className="px-3 py-2 text-right font-medium">CM</th>
                  <th className="px-3 py-2 text-left font-medium">Outcome</th>
                  <th className="px-3 py-2 text-left font-medium">
                    Matched on
                  </th>
                  <th className="px-3 py-2 text-left font-medium">Ref</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isNearest = same(nearest, r);
                  const delta =
                    isNearest && quotedUnitPence != null
                      ? precedentDeltaPct(quotedUnitPence, r)
                      : null;
                  return (
                    <tr
                      key={`${r.reference}-${r.date}`}
                      className={`border-b border-border last:border-0 ${
                        isNearest
                          ? "bg-[color:var(--color-primary)]/5 font-medium"
                          : ""
                      } ${r.outcome === "quoted" ? "text-muted-foreground" : ""}`}
                    >
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        {formatPrecedentDate(r.date)}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {r.quantity.toLocaleString()}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {formatQuotedPrice(r.price_pence)}
                        {r.delivery_pence != null && (
                          <span className="block text-xs font-normal text-muted-foreground">
                            incl. {formatQuotedPrice(r.delivery_pence)} delivery
                          </span>
                        )}
                        {r.unusual_delivery && (
                          <span className="block text-xs font-normal text-amber-700 dark:text-amber-400">
                            {r.unusual_delivery}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {formatUnitPence(r.unit_price_pence)}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {/* A precedent row's margin is percent on the wire (60.0), unlike a price level's. */}
                        {r.contribution_margin_pct == null
                          ? "—"
                          : formatCmPct(r.contribution_margin_pct / 100)}
                      </td>
                      <td className="px-3 py-1.5">
                        {r.outcome === "ordered" ? "Ordered" : "Quoted only"}
                        {r.source === "catalogue" && (
                          <span className="ml-1 text-xs text-muted-foreground">
                            · catalogue
                          </span>
                        )}
                        {same(lastQuoted, r) && (
                          <span className="ml-1 text-xs">· last quoted</span>
                        )}
                        {delta != null && (
                          <span className="ml-2 text-xs">
                            this estimate <DeltaChip pct={delta} />
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-xs text-muted-foreground">
                        {MATCH_LABEL[r.match]}
                      </td>
                      <td className="px-3 py-1.5">
                        <Link
                          to={r.url}
                          className="text-primary hover:underline"
                        >
                          {r.reference}
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {search ? (
            <PrecedentSearchAndLink
              message="Quoted before, but no order recognised for this spec."
              onSearch={search.onSearch}
              onLink={search.onLink}
              linkError={linkError}
            />
          ) : (
            !nearest && (
              <p className="text-xs text-muted-foreground">
                No order within ±{precedent.quantity_tolerance_pct}% of{" "}
                {precedent.quantity.toLocaleString()} in the last{" "}
                {precedent.window_months} months — the rows above are at other
                quantities or were only quoted.
              </p>
            )
          )}
        </>
      )}
    </section>
  );
}
